import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setAuthToken, type ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { me, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const didExchangeRef = useRef(false);

  const entraEnabled = !loading && me.entraEnabled === true;

  useEffect(() => {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const loginCode = params.get('login_code');
    if (!loginCode || didExchangeRef.current) return;
    didExchangeRef.current = true;

    // Remove one-time code from URL hash to avoid accidental replays on re-render/reload.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setBusy(true);
    setError(null);
    api.post<{ ok: boolean; token: string }>('/api/auth/entra/exchange', { code: loginCode })
      .then((resp) => {
        setAuthToken(resp.token);
        return refresh();
      })
      .then(() => {
        navigate('/admin', { replace: true });
      })
      .catch(() => {
        setError('Microsoft sign-in token is invalid or expired. Please try again.');
      })
      .finally(() => {
        setBusy(false);
      });
  }, [navigate, refresh]);

  const entraLogin = () => {
    window.location.href = '/api/auth/entra/login';
  };

  const devLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const resp = await api.post<{ ok: boolean; token: string }>('/api/auth/dev-login', { email });
      setAuthToken(resp.token);
      await refresh();
      navigate('/admin');
    } catch (err) {
      const e = err as ApiError;
      if (e.network) {
        setError(
          'Cannot reach the API server at :4000. Make sure `npm run dev` is running, then try again.'
        );
      } else if (e.status === 403) {
        const body = e.body as { error?: string } | undefined;
        if (body?.error === 'dev-login-disabled') {
          setError(
            'Dev sign-in is disabled in this environment. Use the Microsoft button.'
          );
        } else {
          setError(
            `Sign-in failed. If you believe you should have access, contact your administrator.`
          );
        }
      } else {
        setError(`Login failed (HTTP ${e.status || '?'}). Check the server console.`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-narrow">
      <h1>Admin sign-in</h1>

      {entraEnabled ? (
        <>
          <button className="btn btn-primary" onClick={entraLogin}>
            Sign in with Microsoft
          </button>
          <p className="muted" style={{ marginTop: 16 }}>
            — or, during local development —
          </p>
        </>
      ) : (
        <p className="muted">
          Microsoft sign-in is not configured (set <code>ENTRA_CLIENT_ID</code> and{' '}
          <code>ENTRA_CLIENT_SECRET</code> in <code>.env</code> to enable). Use the dev
          sign-in below.
        </p>
      )}

      <form onSubmit={devLogin} className="redeem-form" style={{ marginTop: 12 }}>
        <input
          type="email"
          placeholder="you@integrella.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Dev sign-in
        </button>
      </form>
      {error && <p className="error" style={{ marginTop: 12 }}>{error}</p>}
    </div>
  );
}
